import React, { useEffect } from "react";
import styled from "styled-components";

// Styled Components
const InstagramContainer = styled.div`
  width: 100%;
  max-height: 500px;
  max-width: 540px;
  margin: 0 auto;
  padding: 0;
  box-sizing: border-box;

  overflow-y: auto;
  overflow-x: hidden;

  @media (max-width: 540px) {
    max-width: 100vw;
    margin: 0;
  }

  @media (max-width: 320px) {
    max-width: 320px;
  }
`;

const InstagramBlockquote = styled.blockquote`
  background: #fff;
  border: 0;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  margin: 0;
  padding: 0;
  width: 100%;
  max-width: 100%;
  min-width: 0; /* Importante para flex shrinking */
  overflow: hidden;
  box-sizing: border-box;

  /* Layout en fila para desktop */
  display: flex;
  flex-direction: row;

  @media (max-width: 768px) {
    flex-direction: column;
    border-radius: 4px;
    box-shadow: 0 1px 8px rgba(0, 0, 0, 0.08);
    margin: 0 8px; /* Margen mínimo en mobile */
    width: calc(100% - 16px); /* Compensar el margen */
  }

  @media (max-width: 375px) {
    margin: 0 4px;
    width: calc(100% - 8px);
  }

  @media (max-width: 320px) {
    margin: 0 2px;
    width: calc(100% - 4px);
    border-radius: 2px;
  }
`;

const VideoContainer = styled.div`
  position: relative;
  background: #000;
  overflow: hidden;
  box-sizing: border-box;

  /* Desktop: tamaño fijo para el video */
  width: 320px;
  height: 320px;
  border-radius: 8px 0 0 8px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    /* Mobile: responsive con aspect ratio */
    width: 100%;
    height: auto;
    padding-bottom: 100%; /* Aspecto cuadrado en móvil */
    border-radius: 4px 4px 0 0;
  }

  @media (max-width: 480px) {
    padding-bottom: 90%;
  }

  @media (max-width: 375px) {
    padding-bottom: 85%;
  }

  @media (max-width: 320px) {
    border-radius: 2px 2px 0 0;
    padding-bottom: 80%; /* Más rectangular en pantallas muy pequeñas */
  }
`;

const VideoPlaceholder = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    45deg,
    #405de6,
    #5851db,
    #833ab4,
    #c13584,
    #e1306c,
    #fd1d1d
  );
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.02);
  }
`;

const PlayButton = styled.div`
  width: 60px;
  height: 60px;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 1);
    transform: scale(1.1);
  }

  &::after {
    content: "";
    width: 0;
    height: 0;
    border-left: 18px solid #333;
    border-top: 12px solid transparent;
    border-bottom: 12px solid transparent;
    margin-left: 4px;
  }

  @media (max-width: 768px) {
    width: 50px;
    height: 50px;

    &::after {
      border-left: 15px solid #333;
      border-top: 10px solid transparent;
      border-bottom: 10px solid transparent;
      margin-left: 3px;
    }
  }

  @media (max-width: 375px) {
    width: 45px;
    height: 45px;

    &::after {
      border-left: 12px solid #333;
      border-top: 8px solid transparent;
      border-bottom: 8px solid transparent;
      margin-left: 2px;
    }
  }

  @media (max-width: 320px) {
    width: 40px;
    height: 40px;

    &::after {
      border-left: 10px solid #333;
      border-top: 6px solid transparent;
      border-bottom: 6px solid transparent;
      margin-left: 2px;
    }
  }
`;

const InstagramLogo = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  width: 24px;
  height: 24px;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 768px) {
    top: 8px;
    right: 8px;
    width: 20px;
    height: 20px;
  }

  @media (max-width: 375px) {
    top: 6px;
    right: 6px;
    width: 18px;
    height: 18px;

    svg {
      width: 12px;
      height: 12px;
    }
  }

  @media (max-width: 320px) {
    top: 4px;
    right: 4px;
    width: 16px;
    height: 16px;

    svg {
      width: 10px;
      height: 10px;
    }
  }
`;

const BottomBar = styled.div`
  background: #fff;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  /* Desktop: panel lateral */
  flex: 1;
  padding: 24px;
  border-radius: 0 8px 8px 0;

  @media (max-width: 768px) {
    /* Mobile: panel inferior */
    padding: 10px 12px;
    border-radius: 0 0 4px 4px;
  }

  @media (max-width: 375px) {
    padding: 8px 10px;
  }

  @media (max-width: 320px) {
    padding: 6px 8px;
    border-radius: 0 0 2px 2px;
  }
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    gap: 8px;
    margin-bottom: 8px;
  }

  @media (max-width: 375px) {
    gap: 6px;
    margin-bottom: 6px;
  }

  @media (max-width: 320px) {
    gap: 4px;
    margin-bottom: 4px;
  }
`;

const UserAvatar = styled.div`
  background: linear-gradient(45deg, #405de6, #833ab4, #e1306c);
  border-radius: 50%;
  flex-shrink: 0;

  /* Desktop: avatar más grande */
  width: 32px;
  height: 32px;

  @media (max-width: 768px) {
    width: 24px;
    height: 24px;
  }

  @media (max-width: 375px) {
    width: 20px;
    height: 20px;
  }
`;

const Username = styled.span`
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  font-weight: 600;
  color: #262626;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  /* Desktop: texto más grande */
  font-size: 16px;
  max-width: 150px;

  @media (max-width: 768px) {
    font-size: 13px;
    max-width: 150px;
  }

  @media (max-width: 375px) {
    font-size: 12px;
    max-width: 120px;
  }

  @media (max-width: 320px) {
    font-size: 11px;
    max-width: 100px;
  }
`;

const PostDescription = styled.p`
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  color: #262626;
  line-height: 1.4;
  margin: 0 0 20px 0;

  /* Solo visible en desktop */
  display: block;
  font-size: 14px;

  @media (max-width: 768px) {
    display: none;
  }

  display: inline-block;
  background: #0095f6;
  color: white;
  text-decoration: none;
  border-radius: 6px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  font-weight: 600;
  transition: background 0.2s ease;
  white-space: nowrap;
  text-align: center;

  /* Desktop: botón más grande */
  padding: 12px 24px;
  font-size: 14px;

  &:hover {
    background: #1877f2;
    text-decoration: none;
    color: white;
  }

  @media (max-width: 768px) {
    padding: 5px 10px;
    font-size: 11px;
    border-radius: 4px;
  }

  @media (max-width: 375px) {
    padding: 4px 8px;
    font-size: 10px;
  }

  @media (max-width: 320px) {
    padding: 3px 6px;
    font-size: 9px;
    border-radius: 2px;
  }
`;

const InstagramVideoEmbed = ({
  permalink = "https://www.instagram.com/reel/DMIW9T_sdSP/?utm_source=ig_embed&utm_campaign=loading",
  username = "@espacio.gerontovida",
  description = "Descubre los mejores consejos para el cuidado y bienestar de nuestros adultos mayores. Contenido especializado en gerontología.",
}) => {
  useEffect(() => {
    // Cargar el script de Instagram si no está ya cargado
    if (window.instgrm) {
      window.instgrm.Embeds.process();
    } else {
      const script = document.createElement("script");
      script.src = "//www.instagram.com/embed.js";
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);

  const handleVideoClick = () => {
    window.open(permalink, "_blank", "noopener,noreferrer");
  };

  return (
    <InstagramContainer>
      <InstagramBlockquote
        className="instagram-media"
        data-instgrm-captioned
        data-instgrm-permalink={permalink}
        data-instgrm-version="14"
      >
        <VideoContainer onClick={handleVideoClick}>
          <VideoPlaceholder>
            <PlayButton />
            <InstagramLogo>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="#333">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
              </svg>
            </InstagramLogo>
          </VideoPlaceholder>
        </VideoContainer>

        <BottomBar>
          <div>
            <UserInfo>
              <UserAvatar />
              <Username>{username}</Username>
            </UserInfo>
            <PostDescription>{description}</PostDescription>
          </div>
        </BottomBar>
      </InstagramBlockquote>
    </InstagramContainer>
  );
};

export default InstagramVideoEmbed;
